import React, { useEffect, useState, useRef, memo, useCallback, useContext } from "react";
import { Row, Col, Empty, Button, Image} from "antd";
import "./TwoDImages.scss";
import { Gallery } from "react-grid-gallery";
import { ReactComponent as ImgOutline } from '../../../../assets/images/black-large-img-outline.svg'
import DottedLoader from "../../DottedLoader";
import {
  QA_IMG_URI,
  LOW_RES_QA_IMG_URI, AI_GENERATED_GLB, DEFAULT_BASE_URL
} from "../../../../environments/env";
import { getDisplayEngineName, getEngineLogo } from "../../Utilities";
// import "@google/model-viewer";
import { FullscreenOutlined, EditOutlined, CaretRightFilled } from "@ant-design/icons";
import TwoDImagesContext from "../../ContextFiles/TwoDImagesContext";
import VideoCard from "./VideoCard";
import playIconSvg from "../../../../assets/images/play-circle-icon.svg";
let isAiCustomer = localStorage.getItem('is_ai_customer') === 'true';

const InfiniteScrollGallery = memo(({ images, loading, onImageClick, onAIImageClick, type, hasAnyFinalImages, useAntdPreview, paginationState }) => {
  const [displayedImages, setDisplayedImages] = useState([]);
  const [remainingCredits, setRemainingCredits] = useState(0);
  const pageRef = useRef(1);
  const pageSize = 20;
  const galleryRef = useRef();
  
  // Get stack state management from context (with fallbacks for when context is not available)
  const context = useContext(TwoDImagesContext);
  const { 
    enterStackState = () => {}, 
    stackState = false, 
    stackImages = [],
    setImageType = () => {}
  } = context || {};




  const loadMoreImages = useCallback(() => {
    if (!images || images.length === 0) return;
    
    const startIndex = (pageRef.current - 1) * pageSize;
    const newImages = images.slice(startIndex, startIndex + pageSize);

    if (newImages.length > 0) {
      const newDisplayedImages = newImages.map((image) => {
        let processedImage = { ...image };
        processedImage.videoUrl = image.src || image.url;

        if (image.image_type == "mp4") {
          processedImage.src = image.mp4Preview;
        }
        else if(image.image_type == "lifestyle" || image.image_type == "silo" || image.image_type == "ai" || image.image_type == "ai_video") {
          // in case of lifestyle of silo image, change image src to low res thumbnail
          // so that thumbnail is displayed in grid gallery preview
          processedImage.src = image.thumbnail;
        }
        
        // Normalize dimensions to ensure consistent aspect ratio (1:1) for grid layout
        // This prevents awkward white space in rows caused by varying image dimensions
        if (type === 'ai_explore') {
          processedImage.width = 1;
          processedImage.height = 1;
        }
        
        return processedImage;
      });
      setDisplayedImages((prev) => [...prev, ...newDisplayedImages]);
      pageRef.current += 1;
    } else {
      console.log("No more images to load.");
    }
  }, [images, type, pageSize]);

  // Track first image ID to detect dataset changes vs appends
  const prevFirstImageIdRef = useRef(null);

  useEffect(() => {
    if (!images || images.length === 0) {
      setDisplayedImages([]);
      pageRef.current = 1;
      prevFirstImageIdRef.current = null;
      return;
    }
    
    // Generate stable ID for first image
    const firstImg = images[0];
    const currentFirstId = firstImg.image_type === 'ai' || firstImg.image_type === 'ai_video'
      ? `${firstImg.image_type}_${firstImg.id}`
      : `${firstImg.image_type}_${firstImg.entity_id}_${firstImg.image_name || firstImg.filename || firstImg.camera_name}`;
    
    // To Avoid UI Flicker, Only reset if first image changed (different dataset) or first load
    if (prevFirstImageIdRef.current !== currentFirstId) {
      prevFirstImageIdRef.current = currentFirstId;
      setDisplayedImages([]);
      pageRef.current = 1;
      loadMoreImages();
    }
  }, [images, loadMoreImages]);


  const handleScroll = useCallback(() => {
    const { scrollTop, scrollHeight, clientHeight } = galleryRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      loadMoreImages();
    }
  }, [loadMoreImages]);

  const handleAIImageClick = (imageData) => {
    const aiImageData = {
      ...imageData,
      selectedImageUrl: imageData.src.replace(LOW_RES_QA_IMG_URI, QA_IMG_URI).replace("_512.", "."),
      renderIndex: 0, // For single images, use index 0
      renders: [imageData.src.replace(LOW_RES_QA_IMG_URI, QA_IMG_URI).replace("_512.", ".")], // Single render array
      prompt_text: imageData.prompt_text || imageData.display_name,
      product_id: imageData.entity_id || null,
      id: imageData.id || imageData.render_id || null
    };
    
    if (onAIImageClick) {
      onAIImageClick(aiImageData);
    }
  };

  const getEngineClass = (engine) => {
    if (!engine) return '';
    const lowerEngine = engine.toString().toLowerCase();
    if (lowerEngine.includes('gemini')) return 'engine-gemini';
    if (lowerEngine.includes('flux')) return 'engine-flux';
    if (lowerEngine.includes('qwen')) return 'engine-qwen';
    if (lowerEngine.includes('claude')) return 'engine-claude';
    if (lowerEngine.includes('gpt')) return 'engine-gpt';
    if (lowerEngine.includes('gpt-image-1.5')) return 'engine-gpt';
    return `engine-${lowerEngine.replace(/[^a-z0-9]/g, '-')}`;
  };

  const CustomThumbnailWithLoader = ({ item, imageProps }) => {
    if (item.image_type === "ai_generated_glb") {
      return (
        <div style={{ position: "relative", minHeight: "100px", minWidth: "100px" }}>
          <FullscreenOutlined className={`carousel-ico fs product-card`}
          onClick={() => onImageClick(item.src)} 
          />
          <model-viewer
            src={(type == "ai_explore" ? "https://d48f7equ64qjl.cloudfront.net/" : DEFAULT_BASE_URL) + item.uri}
            alt={item.caption || "3D Model"}
            auto-rotate
            camera-controls
            ar
            style={{ width: "100%", height: "300px" }}
          />
        </div>
      );
    }
    
    // Handle stacked AI images - only show stack styling when not in stack state and in AI/All tabs
    if (item.image_type === 'ai' && item.isStackDisplay && !stackState && (type === 'ai' || type === 'all')) {
      const engineVal = item.engine;
      const engineDisplayName = getDisplayEngineName(engineVal);
      const engineLogo = getEngineLogo(engineVal);
      // const engineClass = getEngineClass(engineVal); // Removed engine class from stacked card container
      // console.log('Stack Image Engine:', engineVal, 'Class:', engineClass);
      
      return (
        <div 
          className={`stacked-card stacked-card-top-right ai-stacked-image`}
          style={{ marginTop: 0 }}
        >
          <div className="box" style={{ borderRadius: '3px', position: 'relative'}}> 
            <img
              className="ai-image"
              {...imageProps}
              style={{
                ...imageProps.style,
              }}
            />
            {/* Stack count indicator with EditOutlined icon */}
            {item.stackCount >= 1 && (
              <div className="edit-icon-image-stack">
                <EditOutlined style={{ fontSize: '12px', color: 'black' }} />
                <span>{item.stackCount}</span>
              </div>
            )}
            </div>
          </div>

      );
    }

    // AI video now uses the default image flow but with a play icon

    // You can access the original props passed to the image component via imageProps
    const engineVal = item.engine;
    const engineClass = (item.image_type === 'ai') ? getEngineClass(engineVal) : '';
    // const engineDisplayName = getDisplayEngineName(engineVal);
    // const engineLogo = getEngineLogo(engineVal);
    // console.log('Single Image Engine:', engineVal, 'Class:', engineClass, 'Image Type:', item.image_type);
    
    return (
      <div
        className={engineClass}
        style={{ 
          position: "relative", 
          minHeight: "100px", 
          minWidth: "100px",
          width: "100%", // Ensure full width of the container
          height: "100%", // Ensure full height of the container
          cursor: (item.status === 'in-progress' || item.status === 'inprogress') ? "not-allowed" : 'pointer',
          pointerEvents: (item.status === 'in-progress' || item.status === 'inprogress') ? "none" : 'auto'
        }}
      >
        <img
          className={(item.image_type === 'ai' || item.image_type === 'ai_video') ? 'ai-image' : ''}
          {...imageProps} // Spread the original image props to preserve default behavior
          style={{
            ...imageProps.style,
            filter: (item.status === 'in-progress' || item.status === 'inprogress') ? "blur(2px) brightness(.7)" : ''
          }} // Add custom styles
        />
        {item.image_type === 'ai_video' && item.status !== 'inprogress' && (
             // Play icon is handled by ImageOverlay
             null
        )}
      </div>
    );
  };

  // Show loading indicator with count if pagination is active
  if (loading && paginationState && paginationState.loaded > 0) {
    return (
      <div>
        <div
          ref={galleryRef}
          className="comment-input-box__suggestions"
          style={{ overflowY: "auto", height: `${window.innerHeight * 0.8}px` }}
        >
          <Gallery
            onClick={(index) => {
              const imageData = displayedImages[index];
              const { index: clickedIndex, src, image_type, status, is_final_image } = imageData;
              
              if (status === 'in-progress' || status === 'inprogress') {
                return;
              }

              if (imageData.isStackDisplay && imageData.stackImages && !stackState && (type === 'ai' || type === 'all')) {
                enterStackState(imageData.stackImages);
              }
              else if ((image_type === "ai" || image_type === "ai_video") && !is_final_image) {
                handleAIImageClick(imageData);
              } else if (image_type !== "ai_generated_glb") {
                onImageClick(src.replace(LOW_RES_QA_IMG_URI, QA_IMG_URI).replace("_512.", ".")); 
              }
            }}
            rowHeight={300}
            margin={(type === 'ai' || type === 'final_images') ? 8 : 8}
            images={displayedImages}
            enableImageSelection={false}
            thumbnailImageComponent={CustomThumbnailWithLoader}
            tagStyle={{
              display: 'inline-flex',
              alignItems: 'center',
              background: 'transparent',
              color: '#333',
              padding: '0',
              margin: '0',
              cursor: 'default',
              gap: '8px',
              fontWeight: '600'
            }}
          />
        </div>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          padding: '20px',
          gap: '8px'
        }}>
          <DottedLoader custom_class_ai="ai-section" />
          <span className="manrope f-14 w-500 black-085">
            {paginationState.loaded}+ images loaded
          </span>
        </div>
      </div>
    );
  }

  if (loading) return   <div style={{ overflowY: "auto", height: `calc(100vh - 400px)`, width: `calc(100vw - 500px)` }}>
  <DottedLoader custom_class_ai="ai-section" />
</div>;

  if (!displayedImages.length && !(type === "final_images" && hasAnyFinalImages === false)) {
    return (
      <Row>
        <Col span={24}>
          <Empty description="No data available" />
        </Col>
      </Row>
    );
  }
  if (type === "final_images" && hasAnyFinalImages === false)
  {
    return (
      <Row justify="center" align="middle" style={{ marginTop: "5%" }}>
        <Col span={24} style={{ textAlign: "center" }}>
          <ImgOutline />
          <span className="manrope f-20 mt-24" style={{ display: "block" }}>You haven't saved any final images yet</span>
          {isAiCustomer && <span className="manrope f-16 mt-8" style={{ display: "block" }}>You have {remainingCredits} remaining credits</span>}
          <div className="mt-16" style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
            <Button 
              onClick={() => setImageType('ai')}
              className="browse-previews-btn"
            >
              Browse Generations
            </Button>
            
            {isAiCustomer && <Button
              type="primary"
              className="buy-credits-btn"
            >
              Buy More Credits
            </Button>}
          </div>
        </Col>
      </Row>
    );
  }
  
  // If using Ant Design Image Preview for ai_explore
  if (useAntdPreview && type === 'ai_explore') {
    return (
      <div>
        <div
          ref={galleryRef}
          onScroll={handleScroll}
          className="comment-input-box__suggestions"
          style={{ overflowY: "auto", height: `${window.innerHeight * 0.8}px` }}
        >
          <Image.PreviewGroup>
            <Gallery
              onClick={(index) => {
                // Do nothing, let Ant Design handle the preview
              }}
              rowHeight={300}
              margin={8}
              images={displayedImages}
              enableImageSelection={false}
              thumbnailImageComponent={({ item, imageProps }) => {
              
                
                return (
                  <div
                    style={{ 
                      position: "relative", 
                      minHeight: "100px", 
                      minWidth: "100px",
                      width: "100%",
                      height: "100%",
                      cursor: 'pointer'
                    }}
                  >
                    <Image
                      className={item.image_type === 'ai' ? 'ai-image' : ''}
                      {...imageProps}
                      src={item.src?.replace(/(\.[^.]+)$/, "_512$1")}
                      preview={{
                        src: item.src?.replace("_512.", ".")
                      }}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'block'
                      }}
                    />
                  </div>
                );
              }}
              tagStyle={{
                display: 'inline-flex',
                alignItems: 'center',
                background: 'transparent',
                color: '#333',
                padding: '0',
                margin: '0',
                cursor: 'default',
                gap: '8px',
                fontWeight: '600'
              }}
            />
          </Image.PreviewGroup>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <div
        ref={galleryRef}
        onScroll={handleScroll}
        className="comment-input-box__suggestions"
        style={{ overflowY: "auto", height: `${window.innerHeight * 0.8}px` }}
      >
        <Gallery
          onClick={(index) => {
            const imageData = displayedImages[index];
            const { index: clickedIndex, src, image_type, status, is_final_image } = imageData;
            
            // Prevent click action if status is in-progress
            if (status === 'in-progress' || status === 'inprogress') {
              return;
            }

            if (imageData.isStackDisplay && imageData.stackImages && !stackState && (type === 'ai' || type === 'all')) {
              enterStackState(imageData.stackImages);
            }
            else if ((image_type === "ai" || image_type === "ai_video") && !is_final_image) {
              handleAIImageClick(imageData);
            } else if (image_type !== "ai_generated_glb") {
              onImageClick(src.replace(LOW_RES_QA_IMG_URI, QA_IMG_URI).replace("_512.", ".")); 
            }
          }}
          rowHeight={300}
          margin={(type === 'ai' || type === 'final_images') ? 8 : 8}
          images={displayedImages}
          enableImageSelection={false}
          thumbnailImageComponent={CustomThumbnailWithLoader}
          tagStyle={{
            display: 'inline-flex',
            alignItems: 'center',
            background: 'transparent',
            color: '#333',
            padding: '0',
            margin: '0',
            cursor: 'default',
            gap: '8px',
            fontWeight: '600'
          }}
        />
      </div>
    </div>
  );
});

export default InfiniteScrollGallery;
